import { useCallback, useState } from "react";
import { EntityProgressProbe } from "v2/components/FilingsDashboard/EntityProgressProbe";
import { useGetTaxYearsQuery } from "v2/store/taxSetupApi";

const sortDesc = (a: number, b: number): number => b - a;

type Polling = `${number}000`;

type EntityQuestionCount = { answered: number; total: number };

const EMPTY_COUNT: EntityQuestionCount = { answered: 0, total: 0 };

const percent = ({ answered, total }: EntityQuestionCount): number =>
  total === 0 ? 0 : Math.round((answered / total) * 100);

export const useOnboardingProgress = ({
  groupId,
  entityIds,
  polling,
}: {
  groupId: string;
  entityIds: string[];
  polling?: Polling;
}) => {
  const { data: taxYears } = useGetTaxYearsQuery(
    { groupId },
    { skip: !groupId, pollingInterval: Number(polling) }
  );

  const [countById, setCountById] = useState<
    Record<string, EntityQuestionCount>
  >({});

  const handleEntityProgress = useCallback(
    (entityId: string, count: EntityQuestionCount) => {
      setCountById((prev) => {
        const previous = prev[entityId];

        const isSame =
          previous?.answered === count.answered &&
          previous?.total === count.total;

        if (isSame) return prev;

        return { ...prev, [entityId]: count };
      });
    },
    []
  );

  const progressById = Object.fromEntries(
    Object.entries(countById).map(([entityId, count]) => [
      entityId,
      percent(count),
    ])
  );

  const progressValues = entityIds.map((id) => progressById[id] ?? 0);

  const overallProgress =
    progressValues.length === 0
      ? 0
      : Math.round(
          progressValues.reduce((sum, value) => sum + value, 0) /
            progressValues.length
        );

  const isProgressReady = entityIds.every((id) => id in countById);

  const remainingQuestions = entityIds.reduce((sum, id) => {
    const { answered, total } = countById[id] ?? EMPTY_COUNT;
    return sum + Math.max(total - answered, 0);
  }, 0);

  const probes = entityIds.map((id) => {
    const taxYearEntity = taxYears?.entities.find(
      (entity) => entity.uuid === id
    );

    const currentSeason = Number(taxYearEntity?.default_tax_year);
    const trackedYears = (taxYearEntity?.tracked_years ?? []).map(Number);

    return (
      <EntityProgressProbe
        key={id}
        groupId={groupId}
        apiEntityId={id}
        entityId={id}
        seasons={[currentSeason, ...trackedYears].sort(sortDesc)}
        currentSeason={currentSeason}
        onProgress={handleEntityProgress}
      />
    );
  });

  return {
    progressById,
    overallProgress,
    isProgressReady,
    remainingQuestions,
    probes,
  };
};
